import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Add, Remove, Close, LocationOn } from '@mui/icons-material'
import { Space } from '../../Hooks/useRecommendation/useSpaceRecommendations'

interface Props {
  onSkip: () => void
  onRequestVenues: () => void
  disabled: boolean
  requestedCount: number
  shortlistedSpaces: Space[]
  onRemoveVenue: (spaceId: number) => void
}

const ActionBar = ({
  onSkip,
  onRequestVenues,
  disabled,
  requestedCount,
  shortlistedSpaces,
  onRemoveVenue,
}: Props) => {
  const [open, setOpen] = useState(false)

  const venueRows = shortlistedSpaces.map((space) => (
    <VenueRow key={space.id} space={space} onRemove={() => onRemoveVenue(Number(space.id))} />
  ))

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-10 bg-white border-t border-[#f0f0f0]
        shadow-[0px_-4px_25px_0px_rgba(89,89,89,0.25)] rounded-t-2xl`}>
      {shortlistedSpaces.length > 0 && (
        <div className='sm:hidden px-4 pt-2 pb-2'>
          <AnimatePresence initial={false}>
            {open && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className='overflow-hidden'>
                <div
                  className='max-h-[50vh] overflow-y-auto flex flex-col gap-2 px-3 py-3 [&::-webkit-scrollbar]:hidden'
                  style={{ scrollbarWidth: 'none' }}>
                  {venueRows}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <button
            type='button'
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className='w-full flex items-center justify-between gap-3 pt-1 pb-2 cursor-pointer bg-white'>
            <div className='flex items-center gap-3 min-w-0'>
              <div className='flex -space-x-2 shrink-0'>
                {shortlistedSpaces.slice(0, 3).map((space) => (
                  <div
                    key={space.id}
                    className='w-6 h-6 rounded-full border border-white shrink-0 bg-[#f0f0f0] bg-cover bg-center'
                    style={{ backgroundImage: `url("${space.images[0]}")` }}
                  />
                ))}
              </div>
              <span className='text-sm font-semibold text-[#AF231C] truncate'>
                {requestedCount} {requestedCount === 1 ? 'venue' : 'venues'} shortlisted
              </span>
            </div>
            {open ? (
              <Remove className='size-5! text-[#8c8c8c] shrink-0' />
            ) : (
              <Add className='size-5! text-[#8c8c8c] shrink-0' />
            )}
          </button>
        </div>
      )}

      <div className='sm:hidden flex flex-col gap-2 px-4 pt-2 pb-3'>
        <button
          type='button'
          onClick={onRequestVenues}
          disabled={disabled || requestedCount === 0}
          className={`w-full !bg-[#af231c] !text-white rounded-[4px] !px-[16px] !py-[8px] font-[Montserrat]
            !font-semibold !text-[16px] !leading-[24px] cursor-pointer hover:!bg-[#8b1a15] hover:shadow-lg
            disabled:opacity-50 disabled:cursor-not-allowed transition-all`}>
          Request {requestedCount} venue{requestedCount !== 1 ? 's' : ''}
        </button>
        <button
          type='button'
          onClick={onSkip}
          disabled={disabled}
          className={`w-full !border !border-[#595959] !text-[#595959] !bg-transparent rounded-[4px]
            !px-[16px] !py-[8px] font-[Montserrat] !font-semibold !text-[16px] !leading-[24px]
            cursor-pointer hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all`}>
          Skip - Let the experts choose
        </button>
      </div>

      <div className='hidden sm:flex justify-center py-4'>
        <div className='flex flex-col items-center'>
          <AnimatePresence initial={false}>
            {open && shortlistedSpaces.length > 0 && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className='overflow-hidden'>
                <div
                  className={`max-h-[296px] overflow-y-auto p-4 grid grid-cols-2 gap-3
                    w-[90vw] bg-white [&::-webkit-scrollbar]:hidden`}
                  style={{ scrollbarWidth: 'none' }}>
                  {venueRows}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <div className='flex items-center w-[90vw] shrink-0 gap-16'>
            <div className='flex-1 flex justify-end'>
              <button
                type='button'
                onClick={onSkip}
                disabled={disabled}
                className={`!border !border-[#595959] !text-[#595959] !bg-transparent rounded-[4px] !px-[16px] !py-[8px]
                  font-[Montserrat] !font-semibold !text-[16px] !leading-[24px] cursor-pointer hover:shadow-lg
                  disabled:opacity-50 disabled:cursor-not-allowed transition-all`}>
                Skip - Let the experts choose
              </button>
            </div>
            <div className='flex-1 flex flex-row items-center justify-start gap-4'>
              <button
                type='button'
                onClick={onRequestVenues}
                disabled={disabled || requestedCount === 0}
                className={`!bg-[#af231c] !text-white rounded-[4px] !px-[16px] !py-[8px] font-[Montserrat]
                  !font-semibold !text-[16px] !leading-[24px] cursor-pointer hover:!bg-[#8b1a15] hover:shadow-lg
                  disabled:opacity-50 disabled:cursor-not-allowed transition-all`}>
                Request {requestedCount} venue{requestedCount !== 1 ? 's' : ''}
              </button>
              <button
                type='button'
                onClick={() => setOpen((v) => !v)}
                disabled={requestedCount === 0}
                aria-expanded={open}
                className='flex items-center gap-3 cursor-pointer disabled:cursor-default'>
                {shortlistedSpaces.length > 0 && (
                  <div className='flex -space-x-2 shrink-0'>
                    {shortlistedSpaces.slice(0, 3).map((space) => (
                      <div
                        key={space.id}
                        className='w-6 h-6 rounded-full border border-white shrink-0 bg-[#f0f0f0] bg-cover bg-center'
                        style={{ backgroundImage: `url("${space.images[0]}")` }}
                      />
                    ))}
                  </div>
                )}
                <span className='text-sm font-semibold text-[#AF231C] truncate'>
                  {requestedCount} {requestedCount === 1 ? 'venue' : 'venues'} shortlisted
                </span>
                {requestedCount > 0 &&
                  (open ? (
                    <Remove className='size-5! text-[#8c8c8c] shrink-0' />
                  ) : (
                    <Add className='size-5! text-[#8c8c8c] shrink-0' />
                  ))}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const VenueRow = ({ space, onRemove }: { space: Space; onRemove: () => void }) => (
  <div className='flex items-stretch gap-3 bg-white border border-[#8c8c8c] rounded-lg overflow-hidden'>
    <img
      src={space.images[0]}
      alt={space.title}
      className='w-12 shrink-0 self-stretch object-cover'
    />
    <div className='flex-1 min-w-0 py-2'>
      <p className='text-sm font-medium text-[#1a1a1a] truncate'>{space.title}</p>
      {space.venueName && space.venueName !== space.title && (
        <p className='text-xs text-[#595959] truncate'>{space.venueName}</p>
      )}
      <div className='flex items-center gap-1'>
        <LocationOn className='size-3! text-[#8c8c8c]' />
        <p className='text-xs text-[#8c8c8c] truncate'>
          {space.location.city}, {space.location.country}
        </p>
      </div>
    </div>
    <button
      type='button'
      onClick={onRemove}
      className='p-2 !pr-2 cursor-pointer'
      aria-label={`Remove ${space.title}`}>
      <Close className='size-4! text-[#8c8c8c]' />
    </button>
  </div>
)

export default ActionBar
